import { useEffect, useMemo, useRef, useState } from "react";
import { useAuth } from "@clerk/react";
import { useMutation } from "@tanstack/react-query";
import { Loader2 } from "lucide-react";
import { HugeiconsIcon } from "@hugeicons/react";
import {
  Cancel01Icon,
  Add01Icon,
  ImageUploadIcon,
  Attachment01Icon,
  CursorPointer02Icon,
} from "@hugeicons/core-free-icons";
import { createAssetsApi } from "@/lib/api/assets";
import { AssetPickerSheet } from "@/components/assets/AssetPickerSheet";
import type { Asset, AssetRef } from "@/lib/types/assets";
import { usePipelineStore } from "@/lib/stores/pipeline-store";
import { getModuleDefinition } from "@/lib/registry/modules";
import { MODULE_ICONS } from "@/lib/registry/module-icons";
import { DATA_TYPE_INFO } from "@/lib/registry/data-types";
import type { ModuleInput } from "@/lib/types/module";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Slider } from "@/components/ui/slider";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuRadioGroup,
  DropdownMenuRadioItem,
} from "@/components/ui/dropdown-menu";
import { Separator } from "@/components/ui/separator";
import { ScrollArea } from "@/components/ui/scroll-area";
import { cn } from "@/lib/utils";
import { Switch } from "@/components/ui/switch";
import { useModelCatalogStore } from "@/lib/stores/model-catalog-store";
import type { ModelParam } from "@/lib/types/model-catalog";
import { ModelCombobox } from "@/components/models/ModelCombobox";
import {
  getTextBuilderInputs,
  getStructuredOutputHandles,
  type OutputSchemaField,
} from "@/lib/registry/text-builder";
import { getCompositorInputs } from "@/lib/registry/compositor";
import { useExecutionStore } from "@/lib/stores/execution-store";
import { AgentNodeSettings } from "@/components/project/AgentNodeSettings";
import { validateNodeConfig } from "@/lib/types/node-configs";

export function SettingsPanel() {
  const selectedNodeId = usePipelineStore((s) => s.selectedNodeId);
  const nodes = usePipelineStore((s) => s.nodes);
  const edges = usePipelineStore((s) => s.edges);
  const updateNodeConfig = usePipelineStore((s) => s.updateNodeConfig);
  const updateNodeLabel = usePipelineStore((s) => s.updateNodeLabel);
  const removeEdge = usePipelineStore((s) => s.removeEdge);

  const getById = useModelCatalogStore((s) => s.getById);
  const nodeOutput = useExecutionStore((s) =>
    selectedNodeId ? s.nodeOutputs[selectedNodeId] : undefined,
  );

  const selectedNode = useMemo(
    () => nodes.find((n) => n.id === selectedNodeId),
    [nodes, selectedNodeId],
  );

  const moduleDef = useMemo(
    () =>
      selectedNode ? getModuleDefinition(selectedNode.data.module) : undefined,
    [selectedNode],
  );

  const configErrors = useMemo(() => {
    if (!selectedNode) return [];
    return validateNodeConfig(
      selectedNode.data.module,
      selectedNode.data.config as Record<string, unknown>,
    );
  }, [selectedNode]);

  if (!selectedNode || !moduleDef) {
    return (
      <div className="flex h-full flex-col items-center justify-center gap-4 px-4 text-center">
        <div className="flex size-14 items-center justify-center rounded-2xl border border-border/50 bg-black/3 dark:bg-white/4 shadow-sm">
          <HugeiconsIcon
            icon={CursorPointer02Icon}
            size={28}
            className="text-muted-foreground/60"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <h3 className="text-sm font-semibold text-foreground">
            No Node Selected
          </h3>
          <p className="text-xs text-muted-foreground max-w-[200px] leading-relaxed mx-auto">
            Click on any node in the canvas to view and configure its settings.
          </p>
        </div>
      </div>
    );
  }

  const configInputs = moduleDef.inputs.filter((i) => !i.handle);
  const handleInputs =
    moduleDef.module === "text_builder"
      ? getTextBuilderInputs(String(selectedNode.data.config.template ?? ""))
      : moduleDef.module === "compositor"
        ? getCompositorInputs(selectedNode.data.config)
        : moduleDef.inputs.filter((i) => i.handle);
  const handleOutputs = (() => {
    if (
      selectedNode.data.module === "llm_text" &&
      Array.isArray(selectedNode.data.config.output_schema) &&
      (selectedNode.data.config.output_schema as OutputSchemaField[]).length > 0
    ) {
      return getStructuredOutputHandles(
        selectedNode.data.config.output_schema as OutputSchemaField[],
      );
    }
    return moduleDef.outputs.filter((o) => o.handle);
  })();
  const icon = MODULE_ICONS[moduleDef.icon];

  return (
    <ScrollArea className="h-full">
      <div className="flex flex-col gap-6 p-4">
        {/* Header */}
        <div className="flex items-center gap-2">
          {icon && (
            <HugeiconsIcon
              icon={icon}
              size={18}
              className="text-muted-foreground shrink-0"
            />
          )}
          <div className="flex-1 min-w-0">
            <input
              type="text"
              value={selectedNode.data.label}
              onChange={(e) => updateNodeLabel(selectedNode.id, e.target.value)}
              className="w-full bg-transparent text-sm font-bold outline-none border-none focus:ring-0 truncate"
            />
            <p className="text-[11px] text-muted-foreground">
              {moduleDef.description}
            </p>
          </div>
        </div>

        <Separator />

        {/* Config validation errors */}
        {configErrors.length > 0 && (
          <div className="rounded-lg border border-destructive/30 bg-destructive/5 px-3 py-2 flex flex-col gap-1">
            <span className="text-xs font-medium text-destructive">
              Configuration issues
            </span>
            {configErrors.map((err, i) => (
              <span key={i} className="text-xs text-destructive/80">
                {err}
              </span>
            ))}
          </div>
        )}

        {/* Model selector — for AI generation modules */}
        {moduleDef.modelConfig && (
          <div className="flex flex-col gap-1.5">
            <div className="flex items-center gap-1.5">
              <span className="text-sm font-semibold leading-tight">Model</span>
              <span className="text-red-500 text-sm leading-none">*</span>
            </div>
            <ModelCombobox
              categories={moduleDef.modelConfig.categories}
              value={
                selectedNode.data.config.model
                  ? String(selectedNode.data.config.model)
                  : null
              }
              placeholder="Select model..."
              onSelect={(modelId, model) => {
                if (!model) return;
                updateNodeConfig(selectedNode.id, {
                  model: modelId,
                  provider: model.provider,
                });
              }}
            />
          </div>
        )}

        {/* TODO: uncomment to enable Brand Kit feature
        {["llm_text", "image_gen", "audio_tts", "audio_music"].includes(
          selectedNode.data.module,
        ) && (
          <div className="flex items-center justify-between">
            <div className="flex flex-col">
              <span className="text-xs font-medium text-foreground tracking-tight">
                Brand Kit
              </span>
              <span className="text-[10px] text-muted-foreground">
                Apply brand defaults at run time
              </span>
            </div>
            <Switch
              checked={selectedNode.data.config.brand_kit_enabled !== false}
              onCheckedChange={(checked) =>
                updateNodeConfig(selectedNode.id, {
                  brand_kit_enabled: checked,
                })
              }
            />
          </div>
        )}
        */}

        {/* Model-specific parameters — shown when a model is selected */}
        {/* Agent nodes handle their own model params in the AgentNodeSettings section below */}
        {moduleDef.modelConfig && selectedNode.data.module !== "agent_node" &&
          (() => {
            const modelId = selectedNode.data.config.model
              ? String(selectedNode.data.config.model)
              : null;
            const entry = modelId ? getById(modelId) : undefined;
            const allParams = entry?.parameters_schema?.params ?? [];
            // Filter out params that duplicate handle inputs from the module definition
            const handleInputNames = new Set(
              moduleDef.inputs.filter((i) => i.handle).map((i) => i.name),
            );
            const params = allParams.filter(
              (p) => !handleInputNames.has(p.name),
            );
            if (params.length === 0) return null;
            return (
              <div className="flex flex-col gap-3">
                <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                  Model Settings
                </span>
                {params.map((param) => {
                  const connectedEdge = edges.find(
                    (e) =>
                      e.target === selectedNode.id &&
                      e.targetHandle === param.name,
                  );
                  const sourceNode = connectedEdge
                    ? nodes.find((n) => n.id === connectedEdge.source)
                    : undefined;
                  if (connectedEdge && sourceNode) {
                    return (
                      <div key={param.name} className="flex flex-col gap-1">
                        <div className="flex items-center justify-between gap-2">
                          <span className="text-sm font-semibold leading-tight">
                            {param.label}
                          </span>
                          <div className="flex items-center gap-1.5 shrink-0">
                            <ConnectedBadge
                              source={`${sourceNode.data.label} → ${connectedEdge.sourceHandle}`}
                            />
                            <button
                              onClick={() => removeEdge(connectedEdge.id)}
                              className="flex size-5 items-center justify-center rounded-md text-muted-foreground/50 hover:text-destructive hover:bg-destructive/10 transition-colors"
                              title="Disconnect"
                            >
                              <HugeiconsIcon icon={Cancel01Icon} size={12} />
                            </button>
                          </div>
                        </div>
                        {param.description && (
                          <p className="text-xs text-muted-foreground/50">
                            {param.description}
                          </p>
                        )}
                      </div>
                    );
                  }
                  return (
                    <ModelParamField
                      key={param.name}
                      param={param}
                      value={selectedNode.data.config[param.name]}
                      onChange={(value) =>
                        updateNodeConfig(selectedNode.id, {
                          [param.name]: value,
                        })
                      }
                    />
                  );
                })}
              </div>
            );
          })()}

        {/* Config Fields */}
        {configInputs.length > 0 && (
          <div className="flex flex-col gap-3">
            <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
              Configuration
            </span>
            {configInputs.map((input) => (
              <ConfigField
                key={input.name}
                input={input}
                value={selectedNode.data.config[input.name]}
                onChange={(value) =>
                  updateNodeConfig(selectedNode.id, { [input.name]: value })
                }
              />
            ))}
          </div>
        )}

        {/* Handle Inputs — editable when not connected */}
        {handleInputs.length > 0 && (
          <>
            <div className="flex flex-col gap-3">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Inputs
              </span>
              {handleInputs.map((input) => {
                const connectedEdge = edges.find(
                  (e) =>
                    e.target === selectedNode.id &&
                    e.targetHandle === input.name,
                );
                const sourceNode = connectedEdge
                  ? nodes.find((n) => n.id === connectedEdge.source)
                  : undefined;
                if (connectedEdge && sourceNode) {
                  return (
                    <div key={input.name} className="flex flex-col gap-1">
                      <div className="flex items-center justify-between gap-2">
                        <FieldLabel input={input} />
                        <div className="flex items-center gap-1.5 shrink-0">
                          <ConnectedBadge
                            source={`${sourceNode.data.label} → ${connectedEdge.sourceHandle}`}
                          />
                          <button
                            onClick={() => removeEdge(connectedEdge.id)}
                            className="flex size-5 items-center justify-center rounded-md text-muted-foreground/50 hover:text-destructive hover:bg-destructive/10 transition-colors"
                            title="Disconnect"
                          >
                            <HugeiconsIcon icon={Cancel01Icon} size={12} />
                          </button>
                        </div>
                      </div>
                      {input.description && (
                        <p className="text-xs text-muted-foreground/50">
                          {input.description}
                        </p>
                      )}
                    </div>
                  );
                }

                // Unconnected — show editable field
                return (
                  <ConfigField
                    key={input.name}
                    input={input}
                    value={
                      selectedNode.data.config[input.name] ??
                      input.config?.default
                    }
                    onChange={(value) =>
                      updateNodeConfig(selectedNode.id, { [input.name]: value })
                    }
                  />
                );
              })}
            </div>
          </>
        )}

        {/* Structured Output — for llm_text nodes */}
        {selectedNode.data.module === "llm_text" &&
          (() => {
            const schema = Array.isArray(selectedNode.data.config.output_schema)
              ? (selectedNode.data.config.output_schema as OutputSchemaField[])
              : [];
            const isStructured = schema.length > 0;

            return (
              <div className="flex flex-col gap-3">
                <div className="flex items-center justify-between">
                  <div className="flex flex-col">
                    <span className="text-xs font-medium text-foreground tracking-tight">
                      Structured Output
                    </span>
                    <span className="text-[10px] text-muted-foreground">
                      Force output to be JSON
                    </span>
                  </div>
                  <Switch
                    checked={isStructured}
                    onCheckedChange={(checked) => {
                      if (checked) {
                        updateNodeConfig(selectedNode.id, {
                          output_schema: [
                            {
                              _id: crypto.randomUUID(),
                              name: "",
                              type: "string",
                            },
                          ],
                        });
                      } else {
                        updateNodeConfig(selectedNode.id, {
                          output_schema: [],
                        });
                      }
                    }}
                  />
                </div>

                {isStructured && (
                  <div className="flex flex-col gap-2.5 mt-1 p-3 rounded-xl border border-border/50 bg-black/[0.02] dark:bg-white/[0.02]">
                    {schema.map((field, i) => {
                      const isDuplicate =
                        field.name.trim() !== "" &&
                        schema.filter(
                          (f) => f.name.trim() === field.name.trim(),
                        ).length > 1;

                      return (
                        <div
                          key={field._id ?? field.name ?? i}
                          className="flex items-center gap-2 group relative"
                        >
                          <input
                            value={field.name}
                            placeholder="Field name"
                            onChange={(e) => {
                              const next = [...schema];
                              next[i] = { ...field, name: e.target.value };
                              updateNodeConfig(selectedNode.id, {
                                output_schema: next,
                              });
                            }}
                            className={cn(
                              inputClass,
                              "h-8 flex-1 min-w-0 px-2.5 text-xs bg-white dark:bg-black/20 shadow-sm transition-all focus:border-brand/40",
                              isDuplicate
                                ? "border-red-500 focus:border-red-500 ring-1 ring-red-500 text-red-600 dark:text-red-400"
                                : "border-transparent",
                            )}
                          />
                          <select
                            value={field.type}
                            className="h-8 shrink-0 rounded-lg bg-white dark:bg-black/20 border border-border/40 px-2 py-0 text-xs text-foreground outline-none cursor-pointer shadow-sm transition-all focus:border-brand/40 min-w-[70px]"
                            onChange={(e) => {
                              const next = [...schema];
                              next[i] = {
                                ...field,
                                type: e.target
                                  .value as OutputSchemaField["type"],
                              };
                              updateNodeConfig(selectedNode.id, {
                                output_schema: next,
                              });
                            }}
                          >
                            <option value="string">string</option>
                            <option value="number">number</option>
                            <option value="boolean">boolean</option>
                          </select>
                          <button
                            type="button"
                            className="shrink-0 flex size-7 items-center justify-center rounded-lg text-muted-foreground/50 hover:text-red-500 hover:bg-red-500/10 transition-colors"
                            onClick={() =>
                              updateNodeConfig(selectedNode.id, {
                                output_schema: schema.filter((_, j) => j !== i),
                              })
                            }
                            title="Remove field"
                          >
                            <HugeiconsIcon icon={Cancel01Icon} size={14} />
                          </button>
                        </div>
                      );
                    })}

                    {schema.length < 10 && (
                      <button
                        type="button"
                        className="self-start flex items-center gap-1.5 text-xs font-medium text-muted-foreground hover:text-foreground transition-colors mt-1 px-2.5 py-1.5 rounded-lg border border-dashed border-border/60 hover:bg-black/[0.03] dark:hover:bg-white/[0.04]"
                        onClick={() =>
                          updateNodeConfig(selectedNode.id, {
                            output_schema: [
                              ...schema,
                              {
                                _id: crypto.randomUUID(),
                                name: "",
                                type: "string",
                              },
                            ],
                          })
                        }
                      >
                        <HugeiconsIcon icon={Add01Icon} size={14} />
                        Add field
                      </button>
                    )}
                    {schema.length >= 10 && (
                      <span className="text-[10px] text-muted-foreground/60 mt-1 pl-1">
                        Maximum 10 fields allowed.
                      </span>
                    )}
                  </div>
                )}
              </div>
            );
          })()}

        {/* Agent-specific settings — template, tools, max iterations */}
        {selectedNode.data.module === "agent_node" && (
          <AgentNodeSettings
            node={selectedNode}
            onConfigChange={(key, value) =>
              updateNodeConfig(selectedNode.id, { [key]: value })
            }
          />
        )}

        {/* Connections: Outputs */}
        {handleOutputs.length > 0 && (
          <>
            <div className="flex flex-col gap-2">
              <span className="text-xs font-medium text-muted-foreground uppercase tracking-wider">
                Outputs
              </span>
              {handleOutputs.map((output) => {
                const connectedEdges = edges.filter(
                  (e) =>
                    e.source === selectedNode.id &&
                    e.sourceHandle === output.name,
                );
                const info = DATA_TYPE_INFO[output.type];
                const outputValue = nodeOutput?.[output.name];

                return (
                  <div key={output.name} className="flex flex-col gap-1">
                    <div className="flex items-center gap-2 text-xs">
                      <div
                        className="w-2 h-2 rounded-full shrink-0"
                        style={{ background: info.color }}
                      />
                      <span className="text-muted-foreground">
                        {output.name.replace(/_/g, " ")}
                      </span>
                      <span className="ml-auto text-[10px]">
                        {connectedEdges.length > 0 ? (
                          <span className="text-foreground">
                            {connectedEdges.length} connection
                            {connectedEdges.length > 1 ? "s" : ""}
                          </span>
                        ) : (
                          <span className="text-muted-foreground/60">
                            not connected
                          </span>
                        )}
                      </span>
                    </div>
                    {outputValue != null && (
                      <p className="text-[11px] text-muted-foreground/70 break-words leading-relaxed pl-3.5 line-clamp-3">
                        {String(outputValue)}
                      </p>
                    )}
                  </div>
                );
              })}
            </div>
          </>
        )}
      </div>
    </ScrollArea>
  );
}

function FieldLabel({ input }: { input: ModuleInput }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-sm font-semibold capitalize leading-tight">
        {input.name.replace(/_/g, " ")}
      </span>
      {input.required && (
        <span className="text-red-500 text-sm leading-none">*</span>
      )}
    </div>
  );
}

function ConnectedBadge({ source }: { source: string }) {
  return (
    <div
      className="flex items-center gap-1.5 rounded-full border border-emerald-500/40 bg-emerald-500/10 px-2.5 py-0.5 shrink-0"
      title={source}
    >
      <span className="size-1.5 rounded-full bg-emerald-500 shrink-0" />
      <span className="text-[10px] font-medium text-emerald-500 dark:text-emerald-400 truncate max-w-[120px]">
        {source}
      </span>
    </div>
  );
}

const inputClass =
  "rounded-xl border-none bg-black/5 dark:bg-white/[0.08] text-sm placeholder:text-muted-foreground/40 focus-visible:ring-1 focus-visible:ring-ring/50";

function FileDropZone({
  accept,
  value,
  onFileChange,
  onFile,
  icon,
  emptyText,
  onOpen,
}: {
  accept: string;
  value: string | null;
  onFileChange?: (dataUrl: string, fileName: string) => void;
  onFile?: (file: File) => void;
  icon: typeof ImageUploadIcon;
  emptyText: string;
  onOpen?: () => void;
}) {
  const inputRef = useRef<HTMLInputElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  const handleFile = (file: File) => {
    if (onFile) {
      onFile(file);
      return;
    }
    const reader = new FileReader();
    reader.onload = (e) => onFileChange?.(e.target?.result as string, file.name);
    reader.readAsDataURL(file);
  };

  const isImagePreview = value?.startsWith("data:image");

  return (
    <div
      onClick={() => {
        if (onOpen) {
          onOpen();
        } else {
          inputRef.current?.click();
        }
      }}
      onDrop={(e) => {
        e.preventDefault();
        setIsDragging(false);
        const f = e.dataTransfer.files[0];
        if (f) handleFile(f);
      }}
      onDragOver={(e) => {
        e.preventDefault();
        setIsDragging(true);
      }}
      onDragLeave={() => setIsDragging(false)}
      className={cn(
        "flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed cursor-pointer transition-colors overflow-hidden",
        isDragging
          ? "border-blue-400/60 bg-blue-500/5"
          : "border-border/50 hover:border-border/80",
        !isImagePreview &&
          "py-6 px-4 bg-black/[0.03] dark:bg-white/[0.04] hover:bg-black/[0.06] dark:hover:bg-white/[0.07]",
      )}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        className="sr-only"
        onChange={(e) => {
          const f = e.target.files?.[0];
          if (f) handleFile(f);
        }}
      />
      {isImagePreview ? (
        <img
          src={value!}
          alt="Preview"
          className="w-full object-cover max-h-48 rounded-xl pointer-events-none"
        />
      ) : (
        <>
          <HugeiconsIcon
            icon={icon}
            size={value ? 16 : 22}
            className={
              value ? "text-muted-foreground/60" : "text-muted-foreground/30"
            }
          />
          <span className="text-xs text-muted-foreground/60 text-center leading-tight break-all">
            {isDragging ? "Drop to upload" : (value ?? emptyText)}
          </span>
        </>
      )}
    </div>
  );
}

type ImageValue = { dataUrl: string; name: string };

function parseImageValue(value: unknown): ImageValue | null {
  if (!value) return null;
  if (typeof value === "object" && value !== null && "dataUrl" in value)
    return value as ImageValue;
  if (typeof value === "string" && value.startsWith("data:"))
    return { dataUrl: value, name: "Uploaded image" };
  return null;
}

function isAssetRef(v: unknown): v is AssetRef {
  return (
    typeof v === "object" &&
    v !== null &&
    "assetId" in v &&
    "storageKey" in v &&
    "mimeType" in v
  );
}

function ImageConfigField({
  input,
  value,
  onChange,
}: {
  input: ModuleInput;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  const { getToken } = useAuth();
  const assetsApi = useMemo(() => createAssetsApi({ getToken }), [getToken]);
  const [pickerOpen, setPickerOpen] = useState(false);

  const uploadMutation = useMutation({
    mutationFn: (file: File) => assetsApi.upload(file),
    onSuccess: (asset) => {
      onChange({
        assetId: asset.id,
        storageKey: asset.storageKey,
        name: asset.name,
        mimeType: asset.mimeType,
      } satisfies AssetRef);
    },
  });

  const handlePickFromLibrary = (asset: Asset) => {
    onChange({
      assetId: asset.id,
      storageKey: asset.storageKey,
      name: asset.name,
      mimeType: asset.mimeType,
    } satisfies AssetRef);
    setPickerOpen(false);
  };

  const assetRef = isAssetRef(value) ? value : null;
  const legacyImage = !assetRef ? parseImageValue(value) : null;
  const hasValue = assetRef || legacyImage;

  const [assetPreviewUrl, setAssetPreviewUrl] = useState<string | null>(null);
  useEffect(() => {
    if (!assetRef?.assetId) return;
    let objectUrl: string | null = null;
    assetsApi
      .fetchContent(assetRef.assetId)
      .then((blob) => {
        objectUrl = URL.createObjectURL(blob);
        setAssetPreviewUrl(objectUrl);
      })
      .catch(() => setAssetPreviewUrl(null));
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
      setAssetPreviewUrl(null);
    };
  }, [assetRef?.assetId, assetsApi]);

  const previewUrl = assetRef ? assetPreviewUrl : legacyImage?.dataUrl;
  const previewName = assetRef?.name ?? legacyImage?.name ?? "";

  return (
    <div className="flex flex-col gap-1.5">
      <FieldLabel input={input} />
      {hasValue ? (
        <div className="flex items-center gap-3 rounded-xl border border-border/50 bg-black/[0.03] dark:bg-white/[0.04] p-2">
          {previewUrl ? (
            <img
              src={previewUrl}
              alt=""
              className="size-10 rounded-lg object-cover shrink-0"
            />
          ) : (
            <div className="size-10 rounded-lg bg-muted/50 shrink-0 flex items-center justify-center">
              <HugeiconsIcon icon={ImageUploadIcon} size={16} className="text-muted-foreground/40" />
            </div>
          )}
          <span className="flex-1 text-sm text-muted-foreground truncate min-w-0">
            {previewName}
          </span>
          <button
            onClick={() => onChange(null)}
            className="shrink-0 flex size-7 items-center justify-center rounded-lg text-muted-foreground/50 hover:text-destructive hover:bg-destructive/10 transition-colors"
            title="Remove image"
          >
            <HugeiconsIcon icon={Cancel01Icon} size={13} />
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {uploadMutation.isPending ? (
            <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border/50 bg-black/[0.03] dark:bg-white/[0.04] py-6 px-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground/40" />
              <span className="text-xs text-muted-foreground/60">Uploading...</span>
            </div>
          ) : (
            <FileDropZone
              accept="image/*"
              value={null}
              onFile={(file) => uploadMutation.mutate(file)}
              onOpen={() => setPickerOpen(true)}
              icon={ImageUploadIcon}
              emptyText="Drop image or click to browse"
            />
          )}
        </div>
      )}

      <AssetPickerSheet
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        acceptType="image"
        disabledTypes={["audio", "video", "document"]}
        onSelect={handlePickFromLibrary}
      />
    </div>
  );
}

// ModelParamField renders one entry from a model's parameters_schema.params.
function ModelParamField({
  param,
  value,
  onChange,
}: {
  param: ModelParam;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  const label = (
    <span className="text-sm font-semibold leading-tight">{param.label}</span>
  );

  if (param.type === "enum" && param.choices) {
    const current = String(value ?? param.default ?? param.choices[0] ?? "");
    return (
      <div className="flex flex-col gap-1.5">
        {label}
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className={cn(
                "flex h-10 w-full items-center justify-between px-3 text-sm",
                "bg-black/5 dark:bg-white/[0.08] rounded-xl border-none",
                "hover:bg-black/8 dark:hover:bg-white/10 outline-none transition-colors",
              )}
            >
              <span className="truncate">{current || "Select…"}</span>
              <svg
                width="12"
                height="12"
                viewBox="0 0 15 15"
                className="ml-1 shrink-0 opacity-40"
              >
                <path
                  d="M4.93179 5.43179C4.75605 5.60753 4.75605 5.89245 4.93179 6.06819C5.10753 6.24392 5.39245 6.24392 5.56819 6.06819L7.49999 4.13638L9.43179 6.06819C9.60753 6.24392 9.89245 6.24392 10.0682 6.06819C10.2439 5.89245 10.2439 5.60753 10.0682 5.43179L7.81819 3.18179C7.64245 3.00605 7.35753 3.00605 7.18179 3.18179L4.93179 5.43179ZM10.0682 9.56819C10.2439 9.39245 10.2439 9.10753 10.0682 8.93179C9.89245 8.75606 9.60753 8.75606 9.43179 8.93179L7.49999 10.8636L5.56819 8.93179C5.39245 8.75606 5.10753 8.75606 4.93179 8.93179C4.75605 9.10753 4.75605 9.39245 4.93179 9.56819L7.18179 11.8182C7.35753 11.9939 7.64245 11.9939 7.81819 11.8182L10.0682 9.56819Z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            className="min-w-[var(--radix-dropdown-menu-trigger-width)]"
          >
            <DropdownMenuRadioGroup value={current} onValueChange={onChange}>
              {param.choices.map((c) => (
                <DropdownMenuRadioItem key={c} value={c} className="text-sm">
                  {c}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
        {param.description && (
          <p className="text-xs text-muted-foreground/60">
            {param.description}
          </p>
        )}
      </div>
    );
  }

  if (
    (param.type === "float" || param.type === "integer") &&
    param.min !== undefined &&
    param.max !== undefined
  ) {
    const numValue = Number(value ?? param.default ?? param.min);
    const step = param.step ?? (param.type === "float" ? 0.1 : 1);
    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          {label}
          <span className="text-sm text-muted-foreground tabular-nums">
            {numValue}
          </span>
        </div>
        <Slider
          min={param.min}
          max={param.max}
          step={step}
          value={[numValue]}
          onValueChange={([v]) =>
            onChange(param.type === "integer" ? Math.round(v) : v)
          }
        />
        {param.description && (
          <p className="text-xs text-muted-foreground/60">
            {param.description}
          </p>
        )}
      </div>
    );
  }

  if (param.type === "boolean") {
    return (
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          {label}
          {param.description && (
            <p className="text-xs text-muted-foreground/60">
              {param.description}
            </p>
          )}
        </div>
        <Switch
          checked={Boolean(value ?? param.default ?? false)}
          onCheckedChange={(checked) => onChange(checked)}
        />
      </div>
    );
  }

  if (param.type === "image") {
    const imageData = parseImageValue(value);
    return (
      <div className="flex flex-col gap-1.5">
        {label}
        {imageData ? (
          <div className="flex items-center gap-3 rounded-xl border border-border/50 bg-black/[0.03] dark:bg-white/[0.04] p-2">
            <img
              src={imageData.dataUrl}
              alt=""
              className="size-10 rounded-lg object-cover shrink-0"
            />
            <span className="flex-1 text-sm text-muted-foreground truncate min-w-0">
              {imageData.name}
            </span>
            <button
              onClick={() => onChange(null)}
              className="shrink-0 flex size-7 items-center justify-center rounded-lg text-muted-foreground/50 hover:text-destructive hover:bg-destructive/10 transition-colors"
              title="Remove image"
            >
              <HugeiconsIcon icon={Cancel01Icon} size={13} />
            </button>
          </div>
        ) : (
          <FileDropZone
            accept="image/*"
            value={null}
            onFileChange={(dataUrl, name) => onChange({ dataUrl, name })}
            icon={ImageUploadIcon}
            emptyText={param.description ?? "Drop image or click to browse"}
          />
        )}
      </div>
    );
  }

  if (
    param.type === "video" ||
    param.type === "audio" ||
    param.type === "file"
  ) {
    return (
      <div className="flex flex-col gap-1.5">
        {label}
        <FileDropZone
          accept={
            param.type === "video"
              ? "video/*"
              : param.type === "audio"
                ? "audio/*"
                : "*/*"
          }
          value={value ? String(value) : null}
          onFileChange={(_, name) => onChange(name)}
          icon={Attachment01Icon}
          emptyText={param.description ?? "Drop file or click to browse"}
        />
      </div>
    );
  }

  // string / fallback
  const strValue = String(value ?? param.default ?? "");
  if (param.multiline) {
    return (
      <div className="flex flex-col gap-1.5">
        {label}
        <Textarea
          value={strValue}
          onChange={(e) => onChange(e.target.value)}
          placeholder={param.description ?? "Enter a value..."}
          className={cn(inputClass, "min-h-[80px] resize-y py-2.5 px-3")}
        />
      </div>
    );
  }
  return (
    <div className="flex flex-col gap-1.5">
      {label}
      <input
        type={
          param.type === "integer" || param.type === "float" ? "number" : "text"
        }
        value={strValue}
        onChange={(e) => onChange(e.target.value)}
        placeholder={param.description ?? ""}
        className={cn(inputClass, "h-10 px-3")}
      />
      {param.description && (
        <p className="text-xs text-muted-foreground/60">{param.description}</p>
      )}
    </div>
  );
}

function FileConfigField({
  input,
  value,
  onChange,
}: {
  input: ModuleInput;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  const { getToken } = useAuth();
  const assetsApi = useMemo(() => createAssetsApi({ getToken }), [getToken]);
  const [pickerOpen, setPickerOpen] = useState(false);

  const uploadMutation = useMutation({
    mutationFn: (file: File) => assetsApi.upload(file),
    onSuccess: (asset) => {
      onChange({
        assetId: asset.id,
        storageKey: asset.storageKey,
        name: asset.name,
        mimeType: asset.mimeType,
      } satisfies AssetRef);
    },
  });

  const handlePickFromLibrary = (asset: Asset) => {
    onChange({
      assetId: asset.id,
      storageKey: asset.storageKey,
      name: asset.name,
      mimeType: asset.mimeType,
    } satisfies AssetRef);
    setPickerOpen(false);
  };

  const assetRef = isAssetRef(value) ? value : null;
  const displayName = assetRef?.name ?? (value ? String(value) : null);

  return (
    <div className="flex flex-col gap-1.5">
      <FieldLabel input={input} />
      {displayName ? (
        <div className="flex items-center gap-3 rounded-xl border border-border/50 bg-black/[0.03] dark:bg-white/[0.04] p-2">
          <HugeiconsIcon icon={Attachment01Icon} size={16} className="shrink-0 text-muted-foreground/60" />
          <span className="flex-1 text-sm text-muted-foreground truncate min-w-0">
            {displayName}
          </span>
          <button
            onClick={() => onChange(null)}
            className="shrink-0 flex size-7 items-center justify-center rounded-lg text-muted-foreground/50 hover:text-destructive hover:bg-destructive/10 transition-colors"
            title="Remove file"
          >
            <HugeiconsIcon icon={Cancel01Icon} size={13} />
          </button>
        </div>
      ) : (
        <div className="flex flex-col gap-2">
          {uploadMutation.isPending ? (
            <div className="flex flex-col items-center justify-center gap-2 rounded-xl border border-dashed border-border/50 bg-black/[0.03] dark:bg-white/[0.04] py-6 px-4">
              <Loader2 className="h-5 w-5 animate-spin text-muted-foreground/40" />
              <span className="text-xs text-muted-foreground/60">Uploading...</span>
            </div>
          ) : (
            <FileDropZone
              accept="*/*"
              value={null}
              onFile={(file) => uploadMutation.mutate(file)}
              onOpen={() => setPickerOpen(true)}
              icon={Attachment01Icon}
              emptyText="Drop file or click to browse"
            />
          )}
        </div>
      )}

      <AssetPickerSheet
        open={pickerOpen}
        onOpenChange={setPickerOpen}
        disabledTypes={["image"]}
        onSelect={handlePickFromLibrary}
      />
    </div>
  );
}

// Dynamic config field renderer
function ConfigField({
  input,
  value,
  onChange,
}: {
  input: ModuleInput;
  value: unknown;
  onChange: (value: unknown) => void;
}) {
  const { description, config } = input;

  // Dropdown for choices
  if (config?.choices && config.choices.length > 0) {
    const current = String(value ?? config.default ?? "");
    return (
      <div className="flex flex-col gap-1.5">
        <FieldLabel input={input} />
        <DropdownMenu>
          <DropdownMenuTrigger asChild>
            <button
              className={cn(
                "flex h-10 w-full items-center justify-between px-3 text-sm",
                "bg-black/5 dark:bg-white/[0.08] rounded-xl border-none",
                "hover:bg-black/8 dark:hover:bg-white/10 outline-none transition-colors",
              )}
            >
              <span className="truncate">{current || "Select…"}</span>
              <svg
                width="12"
                height="12"
                viewBox="0 0 15 15"
                className="ml-1 shrink-0 opacity-40"
              >
                <path
                  d="M4.93179 5.43179C4.75605 5.60753 4.75605 5.89245 4.93179 6.06819C5.10753 6.24392 5.39245 6.24392 5.56819 6.06819L7.49999 4.13638L9.43179 6.06819C9.60753 6.24392 9.89245 6.24392 10.0682 6.06819C10.2439 5.89245 10.2439 5.60753 10.0682 5.43179L7.81819 3.18179C7.64245 3.00605 7.35753 3.00605 7.18179 3.18179L4.93179 5.43179ZM10.0682 9.56819C10.2439 9.39245 10.2439 9.10753 10.0682 8.93179C9.89245 8.75606 9.60753 8.75606 9.43179 8.93179L7.49999 10.8636L5.56819 8.93179C5.39245 8.75606 5.10753 8.75606 4.93179 8.93179C4.75605 9.10753 4.75605 9.39245 4.93179 9.56819L7.18179 11.8182C7.35753 11.9939 7.64245 11.9939 7.81819 11.8182L10.0682 9.56819Z"
                  fill="currentColor"
                />
              </svg>
            </button>
          </DropdownMenuTrigger>
          <DropdownMenuContent
            align="start"
            className="min-w-[var(--radix-dropdown-menu-trigger-width)]"
          >
            <DropdownMenuRadioGroup
              value={current}
              onValueChange={(v) => onChange(v)}
            >
              {config.choices.map((choice) => (
                <DropdownMenuRadioItem
                  key={choice}
                  value={choice}
                  className="text-sm"
                >
                  {choice}
                </DropdownMenuRadioItem>
              ))}
            </DropdownMenuRadioGroup>
          </DropdownMenuContent>
        </DropdownMenu>
        {description && (
          <p className="text-xs text-muted-foreground/60">{description}</p>
        )}
      </div>
    );
  }

  // Slider for min/max numbers
  if (config?.min !== undefined && config?.max !== undefined) {
    const numValue = Number(value ?? config.default ?? config.min);
    const step = config.max <= 2 ? 0.1 : 1;

    return (
      <div className="flex flex-col gap-2">
        <div className="flex items-center justify-between">
          <FieldLabel input={input} />
          <span className="text-sm text-muted-foreground tabular-nums">
            {numValue}
          </span>
        </div>
        <Slider
          min={config.min}
          max={config.max}
          step={step}
          value={[numValue]}
          onValueChange={([v]) => onChange(v)}
        />
        {description && (
          <p className="text-xs text-muted-foreground/60">{description}</p>
        )}
      </div>
    );
  }

  // Multiline textarea
  if (config?.multiline) {
    return (
      <div className="flex flex-col gap-1.5">
        <FieldLabel input={input} />
        <Textarea
          value={String(value ?? config.default ?? "")}
          onChange={(e) => onChange(e.target.value)}
          placeholder={description ?? "Enter a value..."}
          className={cn(inputClass, "min-h-[80px] resize-y py-2.5 px-3")}
        />
        {description && (
          <p className="text-xs text-muted-foreground/60">{description}</p>
        )}
      </div>
    );
  }

  // Image upload
  if (input.type === "image") {
    return <ImageConfigField input={input} value={value} onChange={onChange} />;
  }

  // File upload
  if (input.type === "file") {
    return <FileConfigField input={input} value={value} onChange={onChange} />;
  }

  // Boolean toggle
  if (input.type === "boolean") {
    const boolValue = Boolean(value ?? config?.default ?? false);
    return (
      <div className="flex flex-col gap-1.5">
        <div className="flex items-center justify-between">
          <FieldLabel input={input} />
          <Switch
            checked={boolValue}
            onCheckedChange={(checked) => onChange(checked)}
          />
        </div>
        {description && (
          <p className="text-xs text-muted-foreground/60">{description}</p>
        )}
      </div>
    );
  }

  // Number input
  if (input.type === "number") {
    return (
      <div className="flex flex-col gap-1.5">
        <FieldLabel input={input} />
        <Input
          type="number"
          value={String(value ?? config?.default ?? "")}
          onChange={(e) => onChange(Number(e.target.value))}
          placeholder={description ?? "Enter a value..."}
          className={cn(inputClass, "h-10")}
        />
        {description && (
          <p className="text-xs text-muted-foreground/60">{description}</p>
        )}
      </div>
    );
  }

  // Default: plain string input
  return (
    <div className="flex flex-col gap-1.5">
      <FieldLabel input={input} />
      <Input
        type="text"
        value={String(value ?? config?.default ?? "")}
        onChange={(e) => onChange(e.target.value)}
        placeholder={description ?? "Enter a value..."}
        className={cn(inputClass, "h-10")}
      />
      {description && (
        <p className="text-xs text-muted-foreground/60">{description}</p>
      )}
    </div>
  );
}
